import { CacheLocation } from '@auth0/auth0-spa-js';
import { ENVIRONMENTS } from 'shared/utils/environment/types';
import {
  getEnvironment,
  isDevEnv,
} from 'shared/utils/environment/determineEnvironment';
import {
  getAuth0DevelopmentClientId,
  getAuth0DevelopmentDomain,
  getAuth0DevelopmentSetup,
} from 'shared/utils/environment/variables';

interface Auth0Config {
  domain: string;
  clientId: string;
  redirectUri: string;
  redirectAfterLoginUrl: string;
  audience: string;
  cacheLocation?: CacheLocation;
}
const devdomain = getAuth0DevelopmentDomain();
const cacheLocation = isDevEnv() ? 'localstorage' : undefined;

const auth0StagingDomain = 'hustro-staging.eu.auth0.com';
const auth0IntegrationDomain = 'hustro-integration.eu.auth0.com';
const auth0ProdDomain = 'auth.hustro.com';
const devEnvDomain = 'http://localhost:3000';
const integrationEnvDomain = 'https://integ.o.hustroapp.com';
const stagingEnvDomain = 'https://stage.o.hustroapp.com';
const prodEnvDomain = 'https://app.hustro.com';
const auth0DevAudience = `https://${devdomain}/api/v2/`;
const auth0IntegrationAudience = `https://${auth0IntegrationDomain}/api/v2/`;
const auth0StagingAudience = `https://${auth0StagingDomain}/api/v2/`;
// This is because even if you set a custom domain the API identifier for
// the /userinfo endpoint remains https://{YOUR_ORIGINAL_AUTH0_DOMAIN}/userinfo.
// https://auth0.com/docs/custom-domains/troubleshoot-custom-domains#-service-not-found-
const auth0ProdAudience = `https://hustro.eu.auth0.com/api/v2/`;

const integrationOnLocalhost: Auth0Config = {
  domain: auth0IntegrationDomain,
  clientId: 'VHjvX2xd1UMq6r2JSEeqPYSfEjaEtRKy',
  redirectUri: `${devEnvDomain}/dashboard`,
  redirectAfterLoginUrl: `${devEnvDomain}/redirect`,
  audience: auth0IntegrationAudience,
  cacheLocation,
};

const stagingOnLocalhost: Auth0Config = {
  domain: auth0StagingDomain,
  clientId: 'DlTNCMemuNlWHBBjPWSNZj2178NbaE3t',
  redirectUri: `${devEnvDomain}/dashboard`,
  redirectAfterLoginUrl: `${devEnvDomain}/redirect`,
  audience: auth0StagingAudience,
  cacheLocation,
};

const integration: Auth0Config = {
  domain: auth0IntegrationDomain,
  clientId: 'ti3gdhPnCQKuoW81gkDLDVoMRa9k9AqN',
  redirectUri: `${integrationEnvDomain}/dashboard`,
  redirectAfterLoginUrl: `${integrationEnvDomain}/redirect`,
  audience: auth0IntegrationAudience,
  cacheLocation,
};

const staging: Auth0Config = {
  domain: auth0StagingDomain,
  clientId: 'JBGpEqyS22aKjZw1gvWA6arDhkAhZx89',
  redirectUri: `${stagingEnvDomain}/dashboard`,
  redirectAfterLoginUrl: `${stagingEnvDomain}/redirect`,
  audience: auth0StagingAudience,
  cacheLocation,
};

const production: Auth0Config = {
  domain: auth0ProdDomain,
  clientId: 'j7JIH7n5Nw5KvQyu72dWPIFVk0qhy5s2',
  redirectUri: `${prodEnvDomain}/dashboard`,
  redirectAfterLoginUrl: `${prodEnvDomain}/redirect`,
  audience: auth0ProdAudience,
  cacheLocation,
};

const getAuth0Config = (): Auth0Config => {
  const environment = getEnvironment();
  switch (environment) {
    case ENVIRONMENTS.DEVELOPMENT:
      const auth0Setup = getAuth0DevelopmentSetup();
      if (auth0Setup === 'staging') {
        return stagingOnLocalhost;
      } else if (auth0Setup === 'integration') {
        return integrationOnLocalhost;
      } else if (auth0Setup === 'custom') {
        const clientId = getAuth0DevelopmentClientId();
        const domain = getAuth0DevelopmentDomain();
        if (!domain) {
          throw new Error(
            'Auth0 domain is required when using custom setup'
          );
        }
        if (!clientId) {
          throw new Error(
            'Auth0 clientId is required when using custom setup'
          );
        }
        return {
          domain,
          clientId,
          redirectUri: `${devEnvDomain}/dashboard`,
          redirectAfterLoginUrl: `${devEnvDomain}/redirect`,
          audience: auth0DevAudience,
        };
      }
      throw new Error('Invalid Auth0 setup');
    case ENVIRONMENTS.INTEGRATION_DEV:
      return integrationOnLocalhost;
    case ENVIRONMENTS.STAGING_DEV:
      return stagingOnLocalhost;
    case ENVIRONMENTS.INTEGRATION:
      return integration;
    case ENVIRONMENTS.STAGING:
      return staging;
    case ENVIRONMENTS.PRODUCTION:
      return production;
  }
};

export const auth0Config = getAuth0Config();
export const authorizationParams = {
  redirect_uri: auth0Config.redirectUri,
  audience: auth0Config.audience,
  scope: 'openid offline_access profile email',
  prompt: 'select_account' as const,
};
